// Single source of truth for business facts shown on the website.
// Only add facts confirmed by the business. Items marked UNVERIFIED in
// docs/seo/01-audit.md must be confirmed before being added here.

export const SITE_URL = 'https://www.quickways.org';

export const BUSINESS = {
  name: 'Quick Way',
  legalName: 'Quick Way',
  phone: '+971555736031',
  phoneDisplay: '+971 55 573 6031',
  whatsapp: '971555736031',
  email: 'info@quickways.org',
  playStore: 'https://play.google.com/store/apps/details?id=com.quickway.customer',
  facebook: 'https://www.facebook.com/dxbshrjdish/',
  logo: `${SITE_URL}/img/logo-512.png`,
  ogImage: `${SITE_URL}/img/og-image.jpg`,
  startingPrice: 30,
  currency: 'AED'
};

export const PACKAGES = [
  {
    id: 'basic',
    name: 'Basic Wash',
    price: 30,
    duration: 'About 30 minutes',
    summary: 'An exterior hand wash for regular upkeep between deeper cleans.',
    bestFor: 'Weekly upkeep of the outside of your car',
    includes: ['Exterior hand wash', 'Wheel cleaning', 'Exterior window cleaning', 'Tyre dressing'],
    excludes: ['Interior vacuum or wipe-down', 'Wax or paint protection'],
    servicePage: '/services/exterior-car-wash'
  },
  {
    id: 'deluxe',
    name: 'Deluxe Wash',
    price: 45,
    duration: 'About 45 minutes',
    summary: 'Inside and out: the Basic Wash plus an interior vacuum and wipe-down.',
    bestFor: 'A complete everyday clean, inside and out',
    includes: ['Everything in Basic Wash', 'Interior vacuum', 'Dashboard wipe', 'Door jamb cleaning', 'Air freshener'],
    excludes: ['Seat shampoo or stain removal', 'Leather conditioning', 'Engine bay cleaning'],
    servicePage: '/services/interior-car-cleaning',
    featured: true
  },
  {
    id: 'premium',
    name: 'Premium Detail',
    price: 65,
    duration: 'About 90 minutes',
    summary: 'Our most thorough package, with clay bar, wax and leather care.',
    bestFor: 'Restoring shine and protecting paint and leather',
    includes: ['Everything in Deluxe Wash', 'Leather conditioning', 'Engine bay cleaning', 'Clay bar treatment', 'Wax protection'],
    excludes: ['Ceramic coating (available as an extra)', 'Headlight restoration (available as an extra)'],
    servicePage: '/services/car-detailing'
  }
];

export const ADDONS = [
  { id: 'pet-hair', name: 'Pet hair removal', price: 50, desc: 'Removes embedded pet hair from seats and carpets.' },
  { id: 'headlight', name: 'Headlight restoration', price: 80, desc: 'Clears cloudy, yellowed headlight lenses.' },
  { id: 'odour', name: 'Odour removal', price: 100, desc: 'Targets lingering smells inside the cabin.' },
  { id: 'ceramic', name: 'Ceramic coating', price: 500, desc: 'A longer-lasting protective layer for your paint.' }
];

export const PLANS = [
  {
    id: 'basic-plan',
    name: 'Basic Plan',
    price: 120,
    washes: '4 Basic Washes a month',
    perWash: 'AED 30 per wash',
    note: 'Same price per wash as booking individually, with priority booking.',
    features: ['4 Basic Washes per month', 'Priority booking', 'WhatsApp support', 'Flexible scheduling']
  },
  {
    id: 'premium-plan',
    name: 'Premium Plan',
    price: 240,
    washes: '8 Deluxe Washes a month',
    perWash: 'AED 30 per Deluxe Wash',
    note: 'AED 120 less than booking 8 Deluxe Washes individually (AED 360).',
    features: ['8 Deluxe Washes per month', 'Priority booking', 'WhatsApp support', 'Free interior refresh', '10% off extras'],
    featured: true
  },
  {
    id: 'vip-plan',
    name: 'VIP Plan',
    price: 500,
    washes: 'Unlimited washes',
    perWash: 'Best for daily drivers and multi-wash weeks',
    note: 'Scheduling terms for unlimited washes are confirmed with you before you subscribe.',
    features: ['Unlimited washes', 'VIP priority booking', 'Dedicated account manager', 'Free monthly detailing', '20% off extras']
  }
];

export const CITIES = [
  {
    id: 'abu-dhabi',
    name: 'Abu Dhabi',
    path: '/mobile-car-wash-abu-dhabi',
    areas: ['Yas Island', 'Saadiyat Island', 'Al Reem Island', 'Khalifa City', 'Al Raha', 'Corniche', 'Masdar City', 'Mohammed Bin Zayed City'],
    title: 'Mobile Car Wash Abu Dhabi | At Home from AED 30 | Quick Way',
    description: 'Mobile car wash in Abu Dhabi at your villa, building or office. Exterior wash AED 30, inside-and-out AED 45, detailing AED 65. Order in the Quick Way app.',
    h1: 'Mobile car wash in Abu Dhabi',
    intro: 'Quick Way washes cars at homes, residential towers and offices across Abu Dhabi, from the islands to the mainland communities. Pick a package, send your location and preferred time, and our team confirms your slot. Order in the app or on WhatsApp.',
    local: [
      {
        h: 'Villas and townhouses',
        p: 'In villa communities such as Khalifa City, Mohammed Bin Zayed City and Al Raha, we usually wash in your driveway or outside your gate. Let us know if the car is in a garage so the team can plan access.'
      },
      {
        h: 'Towers on Al Reem, Saadiyat and Yas',
        p: 'Many residential towers have rules about washing in the car park. Tell us your building name and parking level when you book, and check whether your building management needs to approve car washing in the basement.'
      },
      {
        h: 'Office car parks',
        p: 'Book a wash while you are at work. Share the car park name, level and bay, plus any visitor-access steps, so the team can reach your car without calling you out of a meeting.'
      }
    ]
  },
  {
    id: 'dubai',
    name: 'Dubai',
    path: '/mobile-car-wash-dubai',
    areas: ['Dubai Marina', 'Jumeirah', 'Downtown Dubai', 'Dubai Hills', 'Business Bay'],
    title: 'Mobile Car Wash Dubai | Doorstep from AED 30 | Quick Way',
    description: 'Doorstep car wash in Dubai for apartments, villas and offices. Clear prices from AED 30, interior cleaning and detailing. Order in the Quick Way app.',
    h1: 'Mobile car wash in Dubai',
    intro: 'Quick Way brings the car wash to where your car is already parked in Dubai, whether that is a tower car park in Dubai Marina or Business Bay, or a villa driveway in Dubai Hills. Choose a package in the Quick Way app and our team confirms the time with you.',
    local: [
      {
        h: 'Apartment car parks in Marina, Downtown and Business Bay',
        p: 'Most Dubai towers control what happens in their basements. Tell us the building, parking level and bay number, and check with your building management if car washing needs approval.'
      },
      {
        h: 'Villas in Dubai Hills and Jumeirah',
        p: 'For villas we normally wash in the driveway. If your community has gate access or visitor registration, add the details to your booking so the team is not held at the gate.'
      },
      {
        h: 'Offices and business districts',
        p: 'Have your car cleaned during the working day. Share the car park, level and bay so the team can find the car and you do not need to come down.'
      }
    ]
  }
];

export const SERVICE_PAGES = [
  {
    slug: 'exterior-car-wash',
    packageId: 'basic',
    name: 'Exterior Car Wash',
    title: 'Exterior Car Wash at Home Abu Dhabi & Dubai | Quick Way',
    description: 'Exterior hand car wash at your home or office in Abu Dhabi and Dubai. Wheels, windows and tyre dressing included, AED 30. Order in the Quick Way app.',
    h1: 'Exterior car wash at your home or office',
    intro: 'The Basic Wash is a hand wash of the outside of your car, done where it is parked. It suits drivers who want the dust and sand off their car regularly without driving to a car wash station.',
    sections: [
      {
        h: 'What happens during an exterior wash',
        p: 'The team hand washes the body panels, cleans the wheels, cleans the outside of the windows and finishes with tyre dressing. The interior is not part of this package. Add an interior clean by choosing the Deluxe Wash instead.'
      },
      {
        h: 'How often should you wash the outside of your car in the UAE?',
        p: 'Dust and sand settle quickly in the UAE, especially after windy days. Many drivers who park outdoors book an exterior wash every week. Regular washing also stops grit building up, which reduces the risk of scratching when the car is cleaned.'
      }
    ],
    related: ['interior-car-cleaning', 'car-detailing'],
    faqs: [
      { q: 'Is the interior cleaned in the Basic Wash?', a: 'No. The Basic Wash covers the exterior only. The Deluxe Wash (AED 45) adds an interior vacuum, dashboard wipe, door jamb cleaning and air freshener.' },
      { q: 'How long does an exterior wash take?', a: 'About 30 minutes for a typical car. Larger or very dirty vehicles can take longer.' }
    ]
  },
  {
    slug: 'interior-car-cleaning',
    packageId: 'deluxe',
    name: 'Interior Car Cleaning',
    title: 'Interior Car Cleaning at Home | Abu Dhabi & Dubai | Quick Way',
    description: 'Interior car cleaning at your doorstep in Abu Dhabi and Dubai: vacuum, dashboard and door jambs plus an exterior wash, AED 45. Odour and pet hair extras.',
    h1: 'Interior car cleaning at your doorstep',
    intro: 'The Deluxe Wash cleans your car inside and out. It includes everything in the exterior wash plus an interior vacuum, a dashboard wipe, door jamb cleaning and an air freshener.',
    sections: [
      {
        h: 'What the interior clean covers',
        p: 'Seats, carpets and mats are vacuumed, the dashboard is wiped down and the door jambs are cleaned. It is the right choice for a family car or daily commuter that collects sand, crumbs and dust.'
      },
      {
        h: 'Pet hair, smells and stubborn stains',
        p: 'Add pet hair removal (AED 50) or odour removal (AED 100) when you book. If you need seat shampooing or stain removal, mention it in your booking message so we can confirm before the visit whether it can be done and what it costs.'
      }
    ],
    related: ['exterior-car-wash', 'car-detailing'],
    faqs: [
      { q: 'Do you shampoo the seats?', a: 'Seat shampooing is not part of the Deluxe Wash. Mention it when you book and we will confirm availability and price before the visit.' },
      { q: 'How long does the Deluxe Wash take?', a: 'About 45 minutes for a typical car.' }
    ]
  },
  {
    slug: 'car-detailing',
    packageId: 'premium',
    name: 'Car Detailing',
    title: 'Mobile Car Detailing Abu Dhabi & Dubai | AED 65 | Quick Way',
    description: 'Mobile car detailing at your location: clay bar, wax, leather conditioning and engine bay cleaning for AED 65. Ceramic coating available. Abu Dhabi & Dubai.',
    h1: 'Mobile car detailing in Abu Dhabi and Dubai',
    intro: 'The Premium Detail is our most thorough package. It includes everything in the Deluxe Wash, plus clay bar treatment, wax protection, leather conditioning and engine bay cleaning, all done where your car is parked.',
    sections: [
      {
        h: 'Why clay bar and wax matter in the UAE',
        p: 'Sun, sand and humidity leave contamination on paint that washing alone does not remove. A clay bar treatment lifts it off so the surface is smooth. Wax then adds a protective layer and brings back the gloss.'
      },
      {
        h: 'Leather and engine bay care',
        p: 'Leather seats dry out in the heat. Conditioning helps keep them supple. The engine bay is cleaned to remove built-up dust, which also makes leaks and wear easier to spot.'
      },
      {
        h: 'Ceramic coating and headlight restoration',
        p: 'For longer-lasting paint protection, add ceramic coating (AED 500). Cloudy headlights can be restored for AED 80. Both can be added to a booking.'
      }
    ],
    related: ['interior-car-cleaning', 'exterior-car-wash'],
    faqs: [
      { q: 'How long does the Premium Detail take?', a: 'About 90 minutes for a typical car. Larger vehicles or heavily soiled interiors can take longer.' },
      { q: 'Is ceramic coating included?', a: 'No. Ceramic coating is an extra at AED 500 and can be added when you book.' }
    ]
  }
];

export const FAQS = [
  { q: 'Which areas do you cover?', a: 'We currently serve Abu Dhabi and Dubai. Send your exact location when you book and we will confirm coverage for your address. We do not yet serve Sharjah, Ajman or the northern emirates.' },
  { q: 'How do I book a car wash?', a: 'Order through the Quick Way app on Android, use the booking form on this website, or message us on WhatsApp. Your booking is confirmed when our team replies with your time slot.' },
  { q: 'How much does a mobile car wash cost?', a: 'Basic Wash (exterior) is AED 30, Deluxe Wash (inside and out) is AED 45 and Premium Detail is AED 65. Extras such as odour removal or ceramic coating are priced separately. The final price, including any applicable VAT, is confirmed before your wash.' },
  { q: 'Do prices differ for SUVs and larger vehicles?', a: 'The prices shown are our standard package prices. For large SUVs, vans or heavily soiled vehicles, we confirm the final price with you before the wash starts.' },
  { q: 'How long does a wash take?', a: 'About 30 minutes for the Basic Wash, 45 minutes for the Deluxe Wash and 90 minutes for the Premium Detail. Vehicle size and condition can change this.' },
  { q: 'Can you wash my car in my building car park?', a: 'Often, yes. Some buildings restrict car washing in their car parks, so please check with your building management and share the building name, parking level and bay when you book.' },
  { q: 'How do I pay?', a: 'Available payment options are shown in the app or confirmed with you on WhatsApp when we confirm your booking.' },
  { q: 'Can I reschedule or cancel?', a: 'Yes. Message us on WhatsApp as early as possible. See our cancellation and refund policy for details.' },
  { q: 'How do membership plans work?', a: 'You pay a monthly fee for a set number of washes and get priority booking. Plan terms are confirmed with you on WhatsApp before you subscribe.' },
  { q: 'Do you offer fleet or corporate car washing?', a: 'Yes. We arrange scheduled washes for company vehicles at your office or depot in Abu Dhabi and Dubai. Request a quote on our corporate page.' }
];

export const getPackage = (id) => PACKAGES.find((p) => p.id === id);
export const getServicePage = (slug) => SERVICE_PAGES.find((s) => s.slug === slug);
export const getCity = (id) => CITIES.find((c) => c.id === id);
